import React from 'react';
import {
    List,
    Datagrid,
    DateField,
    TextField,
    FunctionField,
    Filter,
    SelectInput,
    TopToolbar,
    useRefresh,
    useTranslate,
    useListContext,
    RecordContextProvider,
} from 'react-admin';
import {
    Box,
    Button,
    Chip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Phone,
    Email,
    AccessTime,
    CheckCircle,
    HourglassEmpty,
    Cancel as CancelIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

/**
 * Admin list of account-deletion requests.
 *
 * Backend routes:
 *   GET /api/backend/account-deletion-requests
 *   GET /api/backend/account-deletion-requests/:id
 *   POST /api/backend/account-deletion-requests/:id/process
 *
 * Only super-admins see this resource (permission gate in App.tsx).
 */

const maskPhone = (phone: string | null | undefined): string => {
    if (!phone) return '-';
    const s = String(phone);
    return s.length <= 4 ? `${s}****` : `${s.slice(0, 4)}****`;
};

const StatusChip: React.FC<{ status: string }> = ({ status }) => {
    const translate = useTranslate();
    const config = (() => {
        switch (status) {
            case 'pending':
                return {
                    color: 'warning' as const,
                    icon: <HourglassEmpty fontSize="small" />,
                    label: translate('resources.account-deletion-requests.statuses.pending', { _: 'Pending' }),
                };
            case 'completed':
                return {
                    color: 'success' as const,
                    icon: <CheckCircle fontSize="small" />,
                    label: translate('resources.account-deletion-requests.statuses.completed', { _: 'Completed' }),
                };
            case 'cancelled':
                return {
                    color: 'default' as const,
                    icon: <CancelIcon fontSize="small" />,
                    label: translate('resources.account-deletion-requests.statuses.cancelled', { _: 'Cancelled' }),
                };
            default:
                return { color: 'default' as const, icon: undefined, label: status };
        }
    })();
    return <Chip icon={config.icon} label={config.label} color={config.color} size="small" />;
};

const AutoProcessChip: React.FC<{ days: number | null | undefined; status: string }> = ({
    days,
    status,
}) => {
    const translate = useTranslate();
    if (status !== 'pending') return <Typography variant="body2" color="text.secondary">—</Typography>;
    if (days === null || days === undefined) {
        return <Typography variant="body2" color="text.secondary">—</Typography>;
    }
    const overdue = days < 0;
    return (
        <Chip
            icon={<AccessTime fontSize="small" />}
            label={
                overdue
                    ? translate('resources.account-deletion-requests.fields.overdue', {
                          days: -days,
                          _: `${-days}d overdue`,
                      })
                    : translate('resources.account-deletion-requests.fields.daysUntil', {
                          days,
                          _: `${days}d`,
                      })
            }
            color={overdue ? 'error' : days <= 3 ? 'warning' : 'default'}
            size="small"
            variant={overdue ? 'filled' : 'outlined'}
        />
    );
};

const RequestsFilter = (props: any) => {
    const translate = useTranslate();
    return (
        <Filter {...props}>
            <SelectInput
                source="status"
                label={translate('resources.account-deletion-requests.filters.status', { _: 'Status' })}
                alwaysOn
                choices={[
                    { id: 'pending', name: translate('resources.account-deletion-requests.statuses.pending', { _: 'Pending' }) },
                    { id: 'completed', name: translate('resources.account-deletion-requests.statuses.completed', { _: 'Completed' }) },
                    { id: 'cancelled', name: translate('resources.account-deletion-requests.statuses.cancelled', { _: 'Cancelled' }) },
                ]}
            />
        </Filter>
    );
};

const DesktopListActions: React.FC = () => {
    const refresh = useRefresh();
    const translate = useTranslate();
    return (
        <TopToolbar sx={{ gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
                startIcon={<RefreshIcon />}
                onClick={refresh}
                variant="outlined"
                size="small"
            >
                {translate('resources.account-deletion-requests.actions.refresh', { _: 'Refresh' })}
            </Button>
        </TopToolbar>
    );
};

const MobileListActions: React.FC = () => {
    const refresh = useRefresh();
    const translate = useTranslate();
    return (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, width: '100%', p: 0 }}>
            <Button
                startIcon={<RefreshIcon />}
                onClick={refresh}
                size="small"
                variant="outlined"
            >
                {translate('resources.account-deletion-requests.actions.refresh', { _: 'Refresh' })}
            </Button>
        </Box>
    );
};

const MobileRequestsList: React.FC = () => {
    const { data, isLoading } = useListContext();
    const navigate = useNavigate();
    if (isLoading || !data) return null;
    return (
        <Box>
            {data.map((record: any) => (
                <RecordContextProvider key={record.id} value={record}>
                    <Box
                        sx={{
                            py: 1.5,
                            px: 2,
                            borderBottom: '1px solid #F0F0F0',
                            cursor: 'pointer',
                        }}
                        onClick={() => navigate(`/account-deletion-requests/${record.id}/show`)}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                            <StatusChip status={record.status} />
                            <AutoProcessChip days={record.days_until_auto_process} status={record.status} />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mt: 0.5 }}>
                            {record.phone && (
                                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                    <Phone sx={{ fontSize: 14, color: '#999' }} />
                                    <Typography variant="caption" color="text.secondary">
                                        {maskPhone(record.phone)}
                                    </Typography>
                                </Box>
                            )}
                            {record.email && (
                                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                    <Email sx={{ fontSize: 14, color: '#999' }} />
                                    <Typography variant="caption" color="text.secondary">
                                        {record.email}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {record.requested_at ? new Date(record.requested_at).toLocaleString() : ''}
                        </Typography>
                        {record.reason && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                    display: 'block',
                                    mt: 0.5,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {record.reason}
                            </Typography>
                        )}
                    </Box>
                </RecordContextProvider>
            ))}
        </Box>
    );
};

export const AccountDeletionRequestsList: React.FC = () => {
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    return (
        <List
            resource="account-deletion-requests"
            filters={<RequestsFilter />}
            actions={isMobile ? <MobileListActions /> : <DesktopListActions />}
            sort={{ field: 'requested_at', order: 'DESC' }}
            perPage={25}
            title={translate('resources.account-deletion-requests.name', {
                smart_count: 2,
                _: 'Account Deletion Requests',
            })}
            empty={false}
            sx={{
                '& .RaList-content': {
                    backgroundColor: isMobile ? 'transparent' : '#FAFAFA',
                },
                ...(isMobile && {
                    '& .RaList-main': { margin: 0 },
                    '& .RaList-content': {
                        backgroundColor: 'transparent !important',
                        boxShadow: 'none !important',
                        mt: 2,
                    },
                    '& .RaList-actions': {
                        backgroundColor: 'transparent !important',
                        p: '0 !important',
                        minHeight: 'auto !important',
                    },
                }),
            }}
        >
            {isMobile ? (
                <MobileRequestsList />
            ) : (
                <Datagrid
                    rowClick="show"
                    bulkActionButtons={false}
                    sx={{
                        '& .RaDatagrid-headerCell': {
                            backgroundColor: '#F5F5F5',
                            fontWeight: 'bold',
                        },
                        '& .RaDatagrid-row:hover': {
                            backgroundColor: '#F0F7FF',
                        },
                    }}
                >
                    <DateField
                        source="requested_at"
                        label={translate('resources.account-deletion-requests.fields.requestedAt', { _: 'Requested' })}
                        showTime
                    />
                    <FunctionField
                        label={translate('resources.account-deletion-requests.fields.status', { _: 'Status' })}
                        render={(r: any) => <StatusChip status={r.status} />}
                    />
                    <FunctionField
                        label={translate('resources.account-deletion-requests.fields.phone', { _: 'Phone (masked)' })}
                        render={(r: any) => (
                            <Typography variant="body2">{maskPhone(r.phone)}</Typography>
                        )}
                    />
                    <TextField
                        source="email"
                        label={translate('resources.account-deletion-requests.fields.email', { _: 'Email' })}
                        emptyText="—"
                    />
                    <FunctionField
                        label={translate('resources.account-deletion-requests.fields.reason', { _: 'Reason' })}
                        render={(r: any) =>
                            r.reason ? (
                                <Typography
                                    variant="body2"
                                    title={r.reason}
                                    sx={{
                                        maxWidth: 220,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {r.reason}
                                </Typography>
                            ) : (
                                <Typography variant="body2" color="text.secondary">—</Typography>
                            )
                        }
                    />
                    <FunctionField
                        label={translate('resources.account-deletion-requests.fields.autoProcess', { _: 'Auto-process in' })}
                        render={(r: any) => <AutoProcessChip days={r.days_until_auto_process} status={r.status} />}
                    />
                    <DateField
                        source="processed_at"
                        label={translate('resources.account-deletion-requests.fields.processedAt', { _: 'Processed' })}
                        showTime
                        emptyText="—"
                    />
                    <TextField
                        source="processed_by"
                        label={translate('resources.account-deletion-requests.fields.processedBy', { _: 'Processed by' })}
                        emptyText="—"
                    />
                </Datagrid>
            )}
        </List>
    );
};

export default AccountDeletionRequestsList;
